import { parseEntityRef } from '@mainstage/catalog-model';

export const identifierForbiddenCharsRegex = new RegExp(/[ ,.;:]+/g);
export const validEntityNameChars = new RegExp(
  /^([A-Za-z0-9][-A-Za-z0-9_.]*)?[A-Za-z0-9]$/,
);

const DEFAULT_SANITIZE_SEPARATOR = '-';
const ILLEGAL_CONTENT_CHARS_REGEX = /[^a-z0-9]+/g;
const FORBIDDEN_SEPARATOR_CHARS_REGEX = /[ ,;:]+/g;
const DIACRITICS_REGEX = /\p{Diacritic}/gu;

function normalizeIdentifierText(value: string): string {
  return value.normalize('NFD').replace(DIACRITICS_REGEX, '').toLowerCase();
}

function isSeparatorAllowed(separator: string): boolean {
  if (separator === '') {
    return true;
  }
  if (new RegExp(FORBIDDEN_SEPARATOR_CHARS_REGEX.source).test(separator)) {
    return false;
  }
  const hasDiacritics = /\p{Diacritic}/u.test(separator.normalize('NFD'));
  return !hasDiacritics;
}

/**
 * Turns free-text into a lowercase, separator-joined, diacritic-free technical string.
 * Exposed to scaffolder templates as the `sanitizeString` Nunjucks global/filter.
 *
 * @throws Error if `value` is not a non-empty string, or `separator` is invalid.
 */
export function sanitizeString(
  value: string,
  separator: string = DEFAULT_SANITIZE_SEPARATOR,
): string {
  if (typeof value !== 'string' || value.length === 0) {
    throw new Error(
      `sanitizeString: expected a non-empty string, received ${JSON.stringify(
        value,
      )}`,
    );
  }
  if (typeof separator !== 'string' || !isSeparatorAllowed(separator)) {
    throw new Error(
      `sanitizeString: invalid separator ${JSON.stringify(
        separator,
      )} - it must be a string that sanitization would not itself strip (no whitespace, forbidden punctuation, or diacritics)`,
    );
  }

  let result = normalizeIdentifierText(value).replace(
    ILLEGAL_CONTENT_CHARS_REGEX,
    separator,
  );

  if (separator !== '') {
    if (result.startsWith(separator)) {
      result = result.slice(separator.length);
    }
    if (result.endsWith(separator)) {
      result = result.slice(0, result.length - separator.length);
    }
  }

  return result;
}

export function generateIdentifier(
  name: string,
  domain: string | Record<string, any>,
): string | null {
  if (!name || !domain) {
    return null;
  }

  const nameWithoutSpecialCharacters = sanitizeEntityName(name);

  if (typeof domain === 'string') {
    const parsedDomain = domain.startsWith('domain:')
      ? parseEntityRef(domain)
      : parseEntityRef(`domain:${domain}`);
    return `${parsedDomain?.name}.${nameWithoutSpecialCharacters}.0`;
  }

  return `${domain?.metadata?.name}.${nameWithoutSpecialCharacters}.0`;
}

export function generateComponentIdentifier(
  name: string,
  system: string,
): string | null {
  if (!name || !system) {
    return null;
  }

  const nameWithoutSpecialCharacters = sanitizeEntityName(name);

  const parsedSystem = system.startsWith('system:')
    ? parseEntityRef(system)
    : parseEntityRef(`system:${system}`);
  return `${parsedSystem?.name}.${nameWithoutSpecialCharacters}`;
}

function sanitizeEntityName(name: string): string {
  return normalizeIdentifierText(name.trim()).replace(
    identifierForbiddenCharsRegex,
    '-',
  );
}
